// nuolino-admin/src/components/layout/SseNotificationBell.tsx
// Sino de notificacoes no header. Subscreve /api/v1/events/stream
// (via useNotificationsStream) e mostra badge com contador de nao lidas.
// Dropdown com lista das ultimas 10 notificacoes.

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff } from 'lucide-react';
import { Button, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';
import {
  NOTIFICATION_KIND_LABEL,
  useNotificationsStream,
} from '@/features/notifications/notifications-stream';
import { formatRelativeFromNow } from '@/lib/format';

export function SseNotificationBell() {
  const { t, i18n } = useTranslation();
  const { notifications, isConnected, markRead, clear } = useNotificationsStream();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const notification of notifications) {
      if (seenRef.current.has(notification.id)) continue;
      seenRef.current.add(notification.id);
      if (notification.kind === 'chat.message' && !notification.read) {
        const message = notification.payload.message;
        toast({
          title: 'Nova mensagem',
          description:
            typeof message === 'string' && message.trim()
              ? message
              : 'Você recebeu uma nova mensagem no chat.',
          tone: 'info',
        });
      }
    }
  }, [notifications, toast]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const recent = notifications.slice(0, 10);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-alt hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action"
        aria-label={t('nav.notifications')}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {isConnected ? (
          <Bell className="h-4 w-4" aria-hidden="true" />
        ) : (
          <BellOff className="h-4 w-4" aria-hidden="true" />
        )}
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-semibold leading-none text-text-on-primary"
            aria-label={`${unreadCount} nao lidas`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('nav.notifications')}
          className="z-modal absolute right-0 mt-2 w-80 overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-xl"
        >
          <header className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5">
            <p className="text-sm font-semibold text-text">Notificacoes</p>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={clear}
                className="text-xs text-text-muted hover:text-text"
              >
                Limpar tudo
              </button>
            )}
          </header>

          <div className="max-h-80 overflow-y-auto">
            {recent.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-text-muted">
                {isConnected ? 'Aguardando eventos...' : 'Desconectado do stream'}
              </p>
            ) : (
              <ul>
                {recent.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => markRead(n.id)}
                      className={cn(
                        'flex w-full items-start gap-2 border-b border-border-subtle px-4 py-2 text-left text-sm transition-colors last:border-b-0 hover:bg-surface-alt',
                        n.read && 'opacity-60',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                          n.read ? 'bg-neutral-300' : 'bg-primary-500',
                        )}
                        aria-hidden="true"
                      />
                      <div className="flex-1 space-y-0.5">
                        <p className="text-xs font-medium text-text">
                          {NOTIFICATION_KIND_LABEL[n.kind] ?? n.kind}
                        </p>
                        <p className="line-clamp-2 text-xs text-text-muted">
                          {JSON.stringify(n.payload).slice(0, 80)}
                        </p>
                        <p className="text-[10px] text-text-muted">
                          {formatRelativeFromNow(n.timestamp, i18n.language as 'pt-BR' | 'en-US')}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <footer className="border-t border-border-subtle bg-surface-alt p-2">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              <Button variant="ghost" size="sm" className="w-full">
                Ver todas
              </Button>
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
